import { z } from "zod";
import { LetterDraftSchema } from "./letters.js";
import { PatientEducationDraftInputSchema } from "./patient-education-drafts.js";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
export const PatientDocumentContentSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("letter"), letter: LetterDraftSchema }).strict(),
    z.object({ kind: z.literal("handout"), handout: PatientEducationDraftInputSchema }).strict(),
]);
export const PatientDocumentFileSchema = z
    .object({
    bucket: z.literal("patient-documents"),
    objectName: z.string().min(1),
    contentHash: Sha256Schema,
    byteSize: z.number().int().positive(),
    fileName: z.string().min(1).max(240),
    pageCount: z.number().int().positive(),
})
    .strict();
/** Shared chart/encounter metadata; each source retains its own authorized PDF reader. */
export const PatientDocumentSummarySchema = z
    .object({
    id: z.string().min(1),
    rootId: z.string().min(1),
    kind: z.enum(["letter", "handout", "referral", "vault", "incoming"]),
    title: z.string().min(1),
    date: IsoDateTimeSchema,
    author: z.string(),
    encounterId: IdSchema.nullable(),
    version: z.number().int().positive(),
    versionCount: z.number().int().positive(),
    superseded: z.boolean(),
    unavailable: z.boolean(),
    printable: z.boolean(),
    fileName: z.string().min(1),
    category: z.string().optional(),
    effectiveDate: z.string().date().nullable().optional(),
    processingStatus: z.string().optional(),
    reviewStatus: z.string().optional(),
})
    .strict();
export const GeneratedPatientDocumentSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema,
    rootId: IdSchema,
    supersedesId: IdSchema.nullable(),
    version: z.number().int().positive(),
    kind: z.enum(["letter", "handout"]),
    title: z.string().min(1).max(240),
    issuedAt: IsoDateTimeSchema,
    effectiveDate: z.string().date(),
    authorId: IdSchema,
    authorName: z.string(),
    content: PatientDocumentContentSchema.nullable(),
    file: PatientDocumentFileSchema.nullable(),
    provenance: z.record(z.string(), z.unknown()),
    actionKey: z.string().nullable(),
    superseded: z.boolean(),
    versionCount: z.number().int().positive(),
    correctionReason: z.string().nullable(),
})
    .strict();
export const PatientDocumentHistorySchema = z.array(GeneratedPatientDocumentSchema);
export const PatientDocumentIssuanceReceiptSchema = GeneratedPatientDocumentSchema.pick({
    id: true,
    encounterId: true,
    patientId: true,
    rootId: true,
    supersedesId: true,
    version: true,
    issuedAt: true,
}).extend({ file: PatientDocumentFileSchema });
export const PatientDocumentDraftSchema = z
    .object({
    id: IdSchema,
    patientId: IdSchema,
    siteId: IdSchema,
    encounterId: IdSchema,
    sourceDocumentId: IdSchema,
    sourceVersion: z.number().int().positive(),
    version: z.number().int().positive(),
    status: z.enum(["draft", "issued", "discarded"]),
    title: z.string().min(1).max(240),
    content: PatientDocumentContentSchema,
    correctionReason: z.string().max(2000).nullable(),
    chartOnly: z.boolean(),
    updatedAt: IsoDateTimeSchema,
    issuedDocumentId: IdSchema.nullable(),
})
    .strict();
export const CreatePatientDocumentReplacementSchema = z
    .object({
    encounterId: IdSchema.optional(),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const SavePatientDocumentDraftSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    title: z.string().trim().min(1).max(240),
    content: PatientDocumentContentSchema,
    correctionReason: z.string().max(2000).nullable(),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const PatientDocumentDraftCommandSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const IssuePatientDocumentDraftSchema = PatientDocumentDraftCommandSchema.extend({
    expectedEncounterVersion: z.number().int().positive(),
    clinicianReviewed: z.literal(true),
    reviewContextHash: Sha256Schema,
});
export const PatientDocumentReservationSchema = z
    .object({
    id: IdSchema,
    encounterId: IdSchema,
    patientId: IdSchema,
    siteId: IdSchema,
    tenantId: IdSchema,
    issuedAt: IsoDateTimeSchema,
    requestHash: Sha256Schema,
    document: GeneratedPatientDocumentSchema.nullable(),
})
    .strict();
//# sourceMappingURL=generated-patient-documents.js.map